<template>
  <v-container class="py-8 bg-light-blue-main">
    <v-btn
      prepend-icon="mdi-arrow-left"
      @click="router.back()"
      >Zurück</v-btn
    >
    <h1>{{ $t("views.organization.create") }}</h1>

    <v-card>
      <v-card-text>
        <v-form>
          <v-text-field
            data-cy="organization-name"
            class="mb-4 mt-2"
            v-model="name"
            :rules="[required]"
            :label="$t('views.organization.name')" />
          <v-text-field
            data-cy="organization-description"
            v-model="description"
            :label="$t('views.organization.description')" />
        </v-form>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-text>
        <v-data-table
          :headers="userListHeaders"
          :items="userListItems">
          <template v-slot:header.actions="{}">
            <v-btn
              data-cy="add-user"
              @click="addUser()"
              ><v-icon>mdi-plus</v-icon></v-btn
            >
          </template>
          <template v-slot:item.actions="{ item, index }">
            <v-btn
              :data-cy="`edit-user-${item.email.replaceAll(/[^\w]/g, '')}`"
              @click="editUser(item, index)"
              ><v-icon>mdi-pencil</v-icon></v-btn
            >
            <v-btn
              :data-cy="`remove-user-${item.email.replaceAll(/[^\w]/g, '')}`"
              v-if="item.rights !== 1"
              @click="removeUser(index)"
              ><v-icon>mdi-delete</v-icon></v-btn
            >
          </template>
        </v-data-table>
        <v-dialog
          v-model="userDialog.dialog"
          persistent>
          <v-card
            width="60%"
            class="mx-auto">
            <v-card-title>
              {{ $t("views.organization.editUser") }}
            </v-card-title>
            <v-card-text>
              <v-form ref="userForm">
                <v-text-field
                  class="mt-2"
                  data-cy="dialog-user-email"
                  :disabled="userDialog.rights === 1"
                  v-model="userDialog.email"
                  :rules="emailRules"
                  label="Email" />
                <v-select
                  data-cy="dialog-user-rights"
                  class="mt-4"
                  :disabled="userDialog.rights === 1"
                  v-model="userDialog.rights"
                  :items="
                    userRights.filter(
                      (option) => userDialog.rights === 1 || option.value !== 1
                    )
                  ">
                  <template v-slot:item="{ props: itemProps, item }">
                    <v-list-item v-bind="itemProps">
                      <template v-slot:title>
                        {{ item.title }}
                        {{ itemProps.extra }}
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
                <RightsElement
                  data-cy="dialog-user-modules"
                  label="Lizenz"
                  class="mt-4"
                  v-model="userDialog.modules"
                  :options="moduleSelectOptions"></RightsElement>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn
                data-cy="dialog-user-save"
                @click="userDialog.callback()"
                >{{ $t("views.organization.save") }}</v-btn
              >
              <v-btn
                data-cy="dialog-user-cancel"
                @click="userDialog.dialog = false"
                >{{ $t("views.organization.cancel") }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>

    <v-card>
      <ModuleListSlots
        ref="moduleList"
        :allSlots="initialModules"
        :slots="allModules"></ModuleListSlots>
    </v-card>

    <v-card>
      <template v-if="false">
        <v-card-title>Zusammenfassung</v-card-title>
        <v-card-text>
          <!-- Sie haben {{ userLicences }} Nutzer und TODO Module ausgewählt. Alle
        eingeladenen Nutzer erhalten eine Einladungs-Email.
        <br /> -->
          Lizenzen laufen immer einen Kalendermonat. Für ihre gewählten Module
          werden somit sofort
          {{ moduleListRef.getInfos().additionalCharge.additionalPrice }} €
          fällig.
          <br />
          Ab dem nächsten Kalendermonat werden
          {{ moduleListRef.getInfos().monthlyCost }} € monatlich fällig.
          _________________ {{ price }} ----------------- {{ thisMonthPrice }}
        </v-card-text>
      </template>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="router.back()">Abbrechen</v-btn>
        <v-btn
          color="success"
          data-cy="create-organization"
          @click="finalizeCreation"
          >{{ $t("views.organization.createButton") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { computed, Ref, ref, useTemplateRef } from "vue";
import { useUserRules } from "@/validations/user";
import { useCommonRules } from "@/validations/common";
import { useTranslation } from "i18next-vue";
import { useMainStore } from "@/stores/mainStore";
import { useAuthStore } from "@/stores/authStore";
import {
  MODULES,
  ORGANIZATION_RIGHT_OWNER,
  ORGANIZATION_RIGHT_READ,
  organizationRightNames
} from "@/utils/constants";
import RightsElement from "@/components/Elements/RightsElement.vue";
import { useAxiosStore } from "@/stores/axiosStore";
import { useRouter } from "vuetify/lib/composables/router";
import ModuleListSlots from "../Licence/ModuleListSlots.vue";

const router = useRouter();

type User = {
  email: string;
  rights: number;
  modules: number;
};

const mainStore = useMainStore();
const authStore = useAuthStore();
const axiosStore = useAxiosStore();

const { t } = useTranslation();

const { emailRules } = useUserRules(t);
const { required } = useCommonRules(t);

const moduleListRef = useTemplateRef("moduleList");

const name = ref("");
const description = ref("");
const mode = ref(0);
const userListItems: Ref<User[]> = ref([
  {
    email: authStore.user.email,
    rights: ORGANIZATION_RIGHT_OWNER,
    modules: 0
  }
]);

const userLicences = computed(() => {
  return userListItems.value.filter((user) => user.modules > 0).length;
});

const moduleSelectOptions = computed(() => {
  return Object.values(mainStore.modules)
    .map((module) => ({
      title: t("views.licence.modules." + module.name + ".name"),
      value: module.value,
      props: {
        "data-cy": `select-option-${module.name}`,
        requires: module.requires,
        valueName: module.name
      }
    }))
    .filter((option) => Number(option.value) !== 32);
});

const userRights = Object.entries(organizationRightNames).map(
  ([key, value]) => ({
    title: t("other.rights." + value),
    value: Number(key),
    props: {
      "data-cy": `select-option-${value}`,
      extra: t("other.rights." + value + "-extra")
    }
  })
);
const userLicence = [
  {
    title: "yes",
    value: 1
  },
  {
    title: "no",
    value: 0
  }
];

const userListHeaders = [
  { title: t("views.organization.userList.email"), value: "email" },
  {
    title: t("views.organization.userList.rights"),
    key: "rights",
    value: (item) =>
      userRights
        .filter((option) => option.value & item.rights)
        .map((option) => option.title)
        .join(", ")
  },
  {
    title: t("views.organization.userList.licence"),
    key: "modules",
    value: (item) =>
      moduleSelectOptions.value
        .filter((option) => option.value & item.modules)
        .map((option) => option.title)
        .join(", ") || "Keine",
    width: 300
  },
  { title: t("views.organization.userList.actions"), value: "actions" }
];

const userFormRef = useTemplateRef("userForm");

const userDialog = ref({
  dialog: false,
  email: "",
  rights: ORGANIZATION_RIGHT_READ,
  modules: 0,
  callback: () => {}
});

const userListEmails = computed(() => {
  return userListItems.value.map((item) => item.email);
});

const modulesById = computed(() => {
  mainStore.modules.reduce((acc, module) => {
    acc[module.value] = module;
    return acc;
  }, {});
});

const initialModules = computed(() => {
  const result = {};
  for (const module of mainStore.modules) {
    result[module.value] = 0;
  }
  return result;
});

const allModules = computed(() => {
  return userListItems.value.reduce(
    (acc, user) => {
      for (const module of mainStore.modules) {
        acc[module.value] = acc[module.value] || 0;
        acc[module.value] += user.modules & module.value ? 1 : 0;
      }
      return acc;
    },
    { 32: 1 }
  );
});

function addUser() {
  userDialog.value = {
    dialog: true,
    email: "",
    rights: ORGANIZATION_RIGHT_READ,
    modules: 0,
    callback: () => {
      userFormRef.value.validate().then((valid) => {
        if (valid.valid) {
          if (
            userDialog.value.email &&
            !userListEmails.value.includes(userDialog.value.email)
          ) {
            userListItems.value.push({
              email: userDialog.value.email,
              rights: userDialog.value.rights,
              modules: userDialog.value.modules
            });
            userDialog.value.dialog = false;
          }
        }
      });
    }
  };
}

function editUser(item: User, index: number) {
  userDialog.value = {
    dialog: true,
    email: item.email,
    rights: item.rights,
    modules: item.modules,
    callback: () => {
      userListItems.value.splice(index, 1, {
        email: userDialog.value.email,
        rights: userDialog.value.rights,
        modules: userDialog.value.modules
      });
      userDialog.value.dialog = false;
    }
  };
}

function removeUser(index: number) {
  userListItems.value.splice(index, 1);
}

function calculatePrice() {}

const price = computed(() => {
  let price = 0;
  for (const user of userListItems.value) {
    for (const module of moduleSelectOptions.value) {
      if (user.modules & module.value) {
        price += MODULES[module.value].price;
      }
    }
  }
  return price / 100;
});

const thisMonthPrice = computed(() => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const daysInMonth = endOfMonth.getDate();
  const remainingDays = daysInMonth - now.getDate() + 1;

  return (remainingDays / daysInMonth) * price.value;
});

const formattedPrice = computed(() => {
  return (price.value / 100).toFixed(2);
});

const sendModules = computed(() => {
  return Object.fromEntries(
    Object.entries(allModules.value).map(([key, value]) => [
      mainStore.modules.find((module) => Number(module.value) === Number(key))
        .name,
      value
    ])
  );
});

function finalizeCreation() {
  moduleListRef.value.saveModules(create);
}

function create() {
  console.log("create Organization");

  axiosStore.execute(
    {
      method: "POST",
      url: "/api/organization/create",
      data: {
        name: name.value,
        description: description.value,
        modules: sendModules.value,
        users: userListItems.value,
        inviterName: authStore.user.first_name + " " + authStore.user.last_name
      }
    },
    (response) => {
      open(response.data, "_self");
      // mainStore.loadOrganizations();
      // router.push({ name: "profile" });
    }
  );
}
</script>
